import { ITextLocaleMap } from '../models/workflows.models';
import {
	resolveFormStepOptionOrderValue,
	usesOrderIndexedFormStepOptionValues,
} from '../components/modal-workflow-form-step/form-step-options.helper';
import {
	normalizeWorkflowLocaleText,
	resolveWorkflowLocaleText,
	serializeOptionalWorkflowLocaleForApi,
	serializeWorkflowLocaleFormValue,
} from './workflow-locale-text.util';

/** Genera UUID para paso/campo FORM si no existe en el payload. */
export function ensureFormEntityUuid(existing?: string | null): string {
	const value = String(existing ?? '').trim();
	if (value) {
		return value;
	}
	return crypto.randomUUID();
}

/** Lee pasos del config API (`config.steps[]`). */
export function readFormStepsFromConfig(config: Record<string, unknown> | undefined): Record<string, unknown>[] {
	if (!config) {
		return [];
	}
	const steps = config['steps'];
	return Array.isArray(steps) ? (steps as Record<string, unknown>[]) : [];
}

/** Lee campos de un paso (`fields[]`). */
export function readStepFieldsFromApi(step: Record<string, unknown>): Record<string, unknown>[] {
	const fields = step['fields'];
	return Array.isArray(fields) ? (fields as Record<string, unknown>[]) : [];
}

/** Convierte título/label API (mapa i18n) a string para controles internos. */
export function localeOrStringToDisplayText(
	value: string | ITextLocaleMap | null | undefined,
	preferredLang: string = 'es',
): string {
	return resolveWorkflowLocaleText(value, preferredLang);
}

/** Indica payload de paso ya normalizado del FormArray del editor (`step_id`, `step_name`, …). */
export function isInternalFormStepShape(step: Record<string, unknown>): boolean {
	return step['step_id'] !== undefined || step['step_name'] !== undefined;
}

/** Indica payload de paso en contrato API (`order`, `title`, …). */
export function isApiFormStepShape(step: Record<string, unknown>): boolean {
	return step['order'] !== undefined || step['title'] !== undefined;
}

/**
 * Normaliza un paso (API o FormArray interno) al shape del editor de workflow.
 * Al editar un paso guardado, el modal recibe `getRawValue()` interno; no re-mapear como API.
 */
export function patchFormStepToEditor(
	step: Record<string, unknown>,
	allowedLocales: readonly string[],
): Record<string, unknown> {
	if (isInternalFormStepShape(step) && !isApiFormStepShape(step)) {
		const fields = (step['fields'] as Record<string, unknown>[]) ?? [];

		return {
			step_id: step['step_id'] ?? 1,
			uuid: step['uuid'] ?? '',
			step_name: normalizeWorkflowLocaleText(step['step_name'] as string | ITextLocaleMap, allowedLocales),
			step_description: normalizeWorkflowLocaleText(
				step['step_description'] as string | ITextLocaleMap,
				allowedLocales,
			),
			alert_msg: step['alert_msg'],
			fields: fields.map((field) => patchFormFieldFromApi(field, allowedLocales)),
		};
	}

	return patchFormStepFromApi(step, allowedLocales);
}

/** Normaliza paso API → shape interno del FormArray (`step_id`, `step_name`, `fields`, …). */
export function patchFormStepFromApi(
	step: Record<string, unknown>,
	allowedLocales: readonly string[],
): Record<string, unknown> {
	return {
		step_id: step['order'] ?? 1,
		uuid: step['uuid'] ?? '',
		step_name: normalizeWorkflowLocaleText(step['title'] as string | ITextLocaleMap, allowedLocales),
		step_description: normalizeWorkflowLocaleText(
			step['description'] as string | ITextLocaleMap,
			allowedLocales,
		),
		alert_msg: step['alert_msg'],
		fields: readStepFieldsFromApi(step).map((field) => patchFormFieldFromApi(field, allowedLocales)),
	};
}

/** Serializa paso interno → contrato API (`uuid`, `order`, `title`, `fields[]`). */
export function serializeFormStepToApi(
	step: Record<string, unknown>,
	allowedLocales: readonly string[],
	serializeField: (field: Record<string, unknown>) => Record<string, unknown>,
): Record<string, unknown> {
	const stepDescriptionMap = serializeOptionalWorkflowLocaleForApi(step['step_description'], allowedLocales);
	const alertMap = serializeOptionalWorkflowLocaleForApi(step['alert_msg'], allowedLocales);
	const titleMap = serializeWorkflowLocaleFormValue(step['step_name'], allowedLocales);
	const internalFields = (step['fields'] as Record<string, unknown>[]) ?? [];

	return {
		uuid: ensureFormEntityUuid(step['uuid'] as string),
		order: Number(step['step_id'] ?? 1),
		title: titleMap,
		description: stepDescriptionMap,
		alert_msg: alertMap,
		fields: internalFields.map((field) => serializeField(field)),
	};
}

/** Normaliza campo API → valores del FormGroup interno (title/label como string). */
export function patchFormFieldFromApi(
	field: Record<string, unknown>,
	allowedLocales: readonly string[],
): Record<string, unknown> {
	const options = (field['options'] as Record<string, unknown>[]) ?? [];
	const patchedOptions = options.map((opt) => ({
		value: opt['value'],
		label: normalizeWorkflowLocaleText(opt['label'] as string | ITextLocaleMap, allowedLocales),
	}));

	return {
		...field,
		uuid: field['uuid'] ?? '',
		title: normalizeWorkflowLocaleText(field['title'] as string | ITextLocaleMap, allowedLocales),
		options: patchedOptions,
	};
}

/** Serializa campo interno → contrato API (uuid, title i18n, options con label i18n). */
export function serializeFormFieldToApi(
	field: Record<string, unknown>,
	allowedLocales: readonly string[],
): Record<string, unknown> {
	const validators = [...((field['validators'] as string[]) ?? [])];
	const matchOcr = validators.includes('match_ocr') || field['match_ocr'] === true;
	const apiValidators = matchOcr
		? [...validators.filter((v) => v !== 'match_ocr'), 'match_ocr']
		: validators.filter((v) => v !== 'match_ocr');

	const titleInput =
		typeof field['title'] === 'string'
			? normalizeWorkflowLocaleText(field['title'], allowedLocales)
			: field['title'];
	const titleMap = serializeOptionalWorkflowLocaleForApi(titleInput, allowedLocales);
	const serialized: Record<string, unknown> = {
		uuid: ensureFormEntityUuid(field['uuid'] as string),
		id: field['id'],
		name: field['name'],
		type: field['type'],
		col: Number(field['col']),
		validators: apiValidators,
	};

	if (titleMap) {
		serialized['title'] = titleMap;
	}

	if (field['description']) {
		serialized['description'] = field['description'];
	}

	const fieldType = field['type'];
	if (fieldType === 'select' || fieldType === 'radio' || fieldType === 'checkbox') {
		const options = (field['options'] as Record<string, unknown>[]) ?? [];
		const useOrderIndex = usesOrderIndexedFormStepOptionValues(field);
		serialized['options'] = options.map((opt, index) => ({
			value: useOrderIndex ? resolveFormStepOptionOrderValue(index) : opt['value'],
			label: normalizeWorkflowLocaleText(opt['label'] as string | ITextLocaleMap, allowedLocales),
		}));
	}

	if (fieldType === 'checkbox' && field['multiple'] === true) {
		serialized['multiple'] = true;
	}

	return serialized;
}
